import Button from "@/common/components/Button";
import Modal from "@/common/components/Modal";
import { useState } from "react";
import { ModalMessageSimple } from "@/common/components/ModalMessageSimple";
import { useQueryClient } from "@tanstack/react-query";
import { useBillingBillSendBillToIntegrationProcess } from "../../hooks/useBillingBillSendBillToIntegrationProcess";
import { BillResumeData } from "../../types/bills.type";
import { FormTransformers } from "@/common/utils/formTransformers";
import { MessageWithErrorExtras, ModalMessageWithErrorExtras } from "@/common/components/ModalMessageWithErrorExtras";

interface SendToIntegrationProcessModalProps {
  billId: string;
  bill?: BillResumeData;
}

const SendToIntegrationProcessModal = ({ billId, bill }: SendToIntegrationProcessModalProps) => {
  const queryClient = useQueryClient();

  const [isOpen, setIsOpen] = useState(false);

  const [messages, setMessages] = useState<string[] | boolean>(false);
  const [errors, setErrors] = useState<MessageWithErrorExtras | boolean>(false);

  const {
    mutate: sendBillToIntegrationProcess,
    isPending: isSendBullToIntegrationProcessPending,
    isError: sendBillToIntegrationProcessFailed,
  } = useBillingBillSendBillToIntegrationProcess({
    id: billId,
    onSuccess: () => {
      if (!bill?.accepted_by_service_at) {
        setMessages(['Fatura enviada para processamento, aguarde alguns instantes ']);
      } else {
        setMessages(['Fatura reenviada para processamento']);
      }

      setIsOpen(false);
    },
    onError: (error) => {
      FormTransformers.errorsWithExtrasTransformer({ setErrors })(error);
      setIsOpen(false);
    },
  });

  return (
    <>
      <Modal
        isOpen={isOpen}
        setIsOpen={setIsOpen}
        Trigger={(
          <Button
            variant="neutral"
            operationKlassToCheckPermission="Admin::Billing::Bills::SendBillToIntegrationProcess"
            propertiesToCheckPermission={bill?.accepted_by_service_at ? ['bypass_bill_status'] : []}
          >
            {!bill?.accepted_by_service_at ? 'Enviar fatura para processamento' : 'Reenviar fatura para processamento'}
          </Button>
        )}
        showClose={false}
        titleClass="text-2xl"
        subtitleClassName="mt-2 text-white-400"
        title={!bill?.accepted_by_service_at ? 'Enviar fatura para processamento' : 'Reenviar fatura para processamento'}
        subtitle={
          <div className="flex flex-col gap-1">
            {!bill?.accepted_by_service_at && (
              <p>Você tem certeza?</p>
            )}

            {bill?.accepted_by_service_at && (
              <>
                <p>Essa fatura já foi enviada para processamento.</p>
                <p className="text-danger-soft">Você deseja forçar o reenvio?</p>
              </>
            )}
          </div>
        }
        useCancellableFooter
        onConfirm={() => sendBillToIntegrationProcess({ bypass_bill_status: !!bill?.accepted_by_service_at })}
        confirmButtonClassName={!bill?.accepted_by_service_at ? 'bg-primary-blue' : ''}
        confirmButtonIsLoading={isSendBullToIntegrationProcessPending}
      />

      {messages && (
        <ModalMessageSimple
          messages={messages}
          setMessages={setMessages}
          isError={sendBillToIntegrationProcessFailed}
          onClose={() => {
            queryClient.invalidateQueries({ queryKey: ['billingBill', billId] });
          }}
        />
      )}

      {errors && (
        <ModalMessageWithErrorExtras
          errors={errors}
          setErrors={setErrors}
        />
      )}
    </>
  );
};

export default SendToIntegrationProcessModal;
